import { createRlsDb, type RlsSessionContext } from "@gencow/core";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "log" | "warn">;

export type PlatformRlsTargetTable = {
  tableName: string;
  quotedName: string;
  policyName: string;
  using: string;
  check?: string;
};

const ADMIN_OR_SYSTEM = `_gencow_platform_is_admin_or_system()`;
const CURRENT_USER = `_gencow_platform_current_user_id()`;
const OWNED_APP = `EXISTS (SELECT 1 FROM apps a WHERE a.id = app_id AND a.user_id = ${CURRENT_USER})`;

export const PLATFORM_SYSTEM_RLS_CONTEXT: RlsSessionContext = {
  currentUserId: "",
  currentUserRole: "system",
  internalSystem: "true",
};

export const PLATFORM_RLS_TARGET_TABLES: PlatformRlsTargetTable[] = [
  {
    tableName: "apps",
    quotedName: "apps",
    policyName: "gencow_apps_isolation",
    using: `${ADMIN_OR_SYSTEM} OR user_id = ${CURRENT_USER}`,
  },
  {
    tableName: "env_vars",
    quotedName: "env_vars",
    policyName: "gencow_env_vars_app_owner",
    using: `${ADMIN_OR_SYSTEM} OR ${OWNED_APP}`,
  },
  {
    tableName: "deployments",
    quotedName: "deployments",
    policyName: "gencow_deployments_app_owner",
    using: `${ADMIN_OR_SYSTEM} OR ${OWNED_APP}`,
  },
  {
    tableName: "cron_jobs",
    quotedName: "cron_jobs",
    policyName: "gencow_cron_jobs_app_owner",
    using: `${ADMIN_OR_SYSTEM} OR ${OWNED_APP}`,
  },
  {
    tableName: "credits",
    quotedName: "credits",
    policyName: "gencow_credits_app_owner",
    using: `${ADMIN_OR_SYSTEM} OR ${OWNED_APP}`,
  },
  {
    tableName: "usage_snapshots",
    quotedName: "usage_snapshots",
    policyName: "gencow_usage_snapshots_app_owner",
    using: `${ADMIN_OR_SYSTEM} OR ${OWNED_APP}`,
  },
  {
    tableName: "db_backups",
    quotedName: "db_backups",
    policyName: "gencow_db_backups_owner",
    using: `${ADMIN_OR_SYSTEM} OR (user_id = ${CURRENT_USER} AND ${OWNED_APP})`,
  },
  {
    tableName: "cli_tokens",
    quotedName: "cli_tokens",
    policyName: "gencow_cli_tokens_owner",
    using: `${ADMIN_OR_SYSTEM} OR user_id = ${CURRENT_USER}`,
  },
  {
    tableName: "platform_domain_audit_log",
    quotedName: "platform_domain_audit_log",
    policyName: "gencow_platform_domain_audit_admin",
    using: `${ADMIN_OR_SYSTEM}`,
  },
  {
    tableName: "platform_activity_events",
    quotedName: "platform_activity_events",
    policyName: "gencow_platform_activity_events_access",
    using: `${ADMIN_OR_SYSTEM} OR actor_user_id = ${CURRENT_USER} OR target_owner_user_id = ${CURRENT_USER} OR ${OWNED_APP}`,
  },
  {
    tableName: "user_credits",
    quotedName: "user_credits",
    policyName: "gencow_user_credits_owner",
    using: `${ADMIN_OR_SYSTEM} OR user_id = ${CURRENT_USER}`,
  },
  {
    tableName: "user",
    quotedName: `"user"`,
    policyName: "gencow_users_self_or_admin",
    using: `${ADMIN_OR_SYSTEM} OR id = ${CURRENT_USER}`,
  },
  {
    tableName: "session",
    quotedName: `"session"`,
    policyName: "gencow_sessions_self_or_system",
    using: `${ADMIN_OR_SYSTEM} OR user_id = ${CURRENT_USER}`,
  },
  {
    tableName: "account",
    quotedName: `"account"`,
    policyName: "gencow_accounts_self_or_system",
    using: `${ADMIN_OR_SYSTEM} OR user_id = ${CURRENT_USER}`,
  },
];

const PLATFORM_RLS_HELPER_STATEMENTS = [
  `CREATE OR REPLACE FUNCTION _gencow_platform_current_user_id()
RETURNS text
LANGUAGE sql
STABLE
AS $$ SELECT NULLIF(current_setting('app.current_user_id', true), '') $$`,
  `CREATE OR REPLACE FUNCTION _gencow_platform_is_admin_or_system()
RETURNS boolean
LANGUAGE sql
STABLE
AS $$ SELECT COALESCE(current_setting('app.internal_system', true), '') = 'true'
          OR COALESCE(current_setting('app.current_user_role', true), '') = 'admin' $$`,
];

function buildPolicyCheck(table: PlatformRlsTargetTable): string {
  return table.check ?? table.using;
}

export function buildPlatformRlsStatements(tables = PLATFORM_RLS_TARGET_TABLES): string[] {
  const statements = [...PLATFORM_RLS_HELPER_STATEMENTS];
  for (const table of tables) {
    statements.push(
      `ALTER TABLE ${table.quotedName} ENABLE ROW LEVEL SECURITY`,
      `ALTER TABLE ${table.quotedName} FORCE ROW LEVEL SECURITY`,
      `DROP POLICY IF EXISTS ${table.policyName} ON ${table.quotedName}`,
      `CREATE POLICY ${table.policyName} ON ${table.quotedName}
  FOR ALL
  USING (${table.using})
  WITH CHECK (${buildPolicyCheck(table)})`,
    );
  }
  return statements;
}

async function listExistingPublicTables(rawSql: RawSql): Promise<Set<string>> {
  const result = await rawSql(
    `SELECT table_name FROM information_schema.tables WHERE table_schema = 'public'`,
  );
  const rows = Array.isArray(result) ? result : [];
  return new Set(
    rows
      .map((row) => (row && typeof row === "object" ? (row as { table_name?: unknown }).table_name : null))
      .filter((name): name is string => typeof name === "string"),
  );
}

export async function ensurePlatformRlsPolicies(params: {
  rawSql: RawSql;
  isPlatform: boolean;
  logger?: LoggerLike;
}): Promise<void> {
  if (!params.isPlatform) return;
  const logger = params.logger ?? console;
  const existing = await listExistingPublicTables(params.rawSql);
  const tables = PLATFORM_RLS_TARGET_TABLES.filter((table) => existing.has(table.tableName));
  const missing = PLATFORM_RLS_TARGET_TABLES.filter((table) => !existing.has(table.tableName));

  for (const statement of buildPlatformRlsStatements(tables)) {
    await params.rawSql(statement);
  }

  if (missing.length > 0) {
    logger.warn(
      `[db] platform RLS skipped missing table(s): ${missing.map((table) => table.tableName).join(", ")}`,
    );
  }
  logger.log(`[db] platform RLS policies ensured for ${tables.length} table(s)`);
}

export function normalizePlatformRlsRole(role: unknown): string | undefined {
  return typeof role === "string" && role.trim() ? role.trim() : undefined;
}

export function buildPlatformRequestRlsContext(params: {
  userId: string;
  role?: unknown;
  isPlatform?: boolean;
}): RlsSessionContext {
  if (!params.isPlatform) {
    return { currentUserId: params.userId };
  }
  const role = normalizePlatformRlsRole(params.role);
  return {
    currentUserId: params.userId,
    ...(role ? { currentUserRole: role } : {}),
    internalSystem: "false",
  };
}

export function createPlatformSystemDb<TDb>(
  db: TDb,
  options: {
    isPlatform: boolean;
    createRlsDbImpl?: typeof createRlsDb;
  },
): TDb {
  if (!options.isPlatform) return db;
  return (options.createRlsDbImpl ?? createRlsDb)(db as never, PLATFORM_SYSTEM_RLS_CONTEXT) as TDb;
}
